import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEqual } from "lodash";
import moment from "moment";

import { useStateSelector } from "utils/field";
import { CodeDrawer as CodeDrawerForm } from "utils/codeLib";

import { actions } from "features/Model/Enclosure/_ducks";
import { change } from "store/root/actions";

import Drawer from "components/Drawer";
import InputRow from "components/Input/Row";
import Select from "components/Input/Select";

import globalStyles from "components/globalStyles.module.scss";
import classes from "./style.module.scss";

import EditIcon from "assets/images/icons/JSX/Edit";

const { updateCodeDrawer } = actions;

const drawerContent = ({ action, name, lastEdited }) =>
    ({
        create: {
            title: `Create ${name} Code`,
            subTitle: `Define the details of your new ${name.toLowerCase()} code`,
            icon: () => <EditIcon fill="#0eb0a9" />,
            introColor: "rgba(24, 193, 173, 0.1)",
        },
        edit: {
            title: `Edit ${name} Code`,
            subTitle: `Last Modified: ${lastEdited ? moment(lastEdited).format("MMM DD, YYYY") : "-"}`,
            icon: () => <EditIcon fill="#0eb0a9" />,
            introColor: "rgba(24, 193, 173, 0.1)",
        },
    }[action]);

const CodeTypeSelect = ({ codeType, toggleCodeType }) => (
    <Select
        className={globalStyles.typeSelect}
        name={"codeType"}
        label="Code Type"
        placeholder="Choose Code Type"
        options={[
            {
                label: "Standard",
                value: "S",
            },
            {
                label: "User Defined",
                value: "U",
            },
        ]}
        disabled={false}
        setValue={codeType}
        input={{
            onChange: (value) => {
                toggleCodeType(value);
            },
            value: codeType,
        }}
    />
);

const CodeDrawer = React.memo(
    ({
        upgradeWhereToAddCode,
        updateCodeDrawer,
        change,
    }) => {
        //TODO: Save last edited to model (and get code library edited??)
        // Or we just add a different description
        const {
            id,
            isOpen = false,
            codeName = "",
            isEditing,
            componentType = "",
            codeType = "S",
            fieldAccessor,
            componentId,
            isDrawing = false,
            windowAccessor = "",
            drawingSave,
            lastEdited,
        } = useStateSelector(`enclosure.codeDrawer`) || {};
        
        const {
            title,
            subTitle,
            icon: Icon,
            introColor,
        } = drawerContent({
            action: isEditing ? "edit" : "create",
            name: codeName,
            lastEdited: lastEdited || "",
        });

        const [isClicked, setIsClicked] = useState(false);

        const toggleOpen = useCallback((value) => updateCodeDrawer({ isOpen: value }), [updateCodeDrawer]);
        const toggleCodeType = useCallback((value) => updateCodeDrawer({ codeType: value }), [updateCodeDrawer]);

        const handleClose = useCallback(() => {
            setIsClicked(false)
            toggleOpen(false);
            toggleCodeType("S");
        }, [toggleCodeType, toggleOpen]);

        const handleUdefSave = useCallback(() => {
            toggleCodeType("S");
            toggleOpen(false);
        }, []);

        return (
            <Drawer
                open={isOpen}
                title={title}
                subtitle={subTitle}
                close={() => {
                    !isClicked ? setIsClicked(true) : handleClose();
                }}
                icon={Icon}
                introColor={introColor}
            >
                {isClicked && (
                    <div className={classes.warningPane} onClick={() => setIsClicked(false)}>
                        <div className={classes.warning}>
                            <p>
                                Click off of the drawer twice to <strong>Discard Changes</strong>
                            </p>
                            <p style={{ fontSize: "15px" }}>Click here to continue editing</p>
                        </div>
                    </div>
                )}
                <CodeDrawerForm
                    toggleOpen={toggleOpen}
                    componentId={componentId}
                    fieldAccessor={fieldAccessor}
                    componentType={componentType}
                    codeType={codeType}
                    handleClose={handleClose}
                    isFlat={componentType === "CeilingFlat"}
                    handleUdefSave={handleUdefSave}
                    change={change}
                    windowAccessor={windowAccessor}
                    drawingSave={drawingSave}
                    upgradeWhereToAddCode={upgradeWhereToAddCode}
                    CodeTypeSelect={!isEditing ? () => <CodeTypeSelect codeType={codeType} toggleCodeType={toggleCodeType} /> : null}
                />
            </Drawer>
        );
    },
    isEqual
);


const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CodeDrawer);